import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  postReply,
  type ConversationDetail,
  type Message,
} from '@/lib/api';
import { conversationQueryKey } from './useConversation';
import { enqueueReply } from '@/lib/replyQueue';

interface ReplyVariables {
  body: string;
}

interface QueuedSendinel {
  __queued: true;
  optimisticId: string;
}

function makeQueuedMessage(optimisticId: string): Message & QueuedSendinel {
  return {
    __queued: true,
    optimisticId,
    id: optimisticId,
    body: '',
    direction: 'out',
    source: 'sms',
    timestamp_iso: new Date().toISOString(),
    sender_name: 'You',
  };
}

interface ReplyContext {
  optimisticId: string;
  previous: ConversationDetail | undefined;
}

/**
 * useReply — optimistic SMS reply send.
 *
 * On submit:
 *   1. Snapshot the cached ConversationDetail.
 *   2. Append a temp message with id=`optimistic_<ts>` and source='sms', direction='out'.
 *   3. Fire the actual POST.
 * On success:
 *   - Replace the optimistic message with the server-echoed Message (real id, server timestamp).
 * On error:
 *   - Roll the cache back to the snapshot, then rethrow so the view can toast + offer retry.
 *
 * The view detects the optimistic message by id-prefix `optimistic_` and renders it
 * with a "sending…" status badge.
 */
export function useReply(slug: string, conversationId: string) {
  const qc = useQueryClient();
  const key = conversationQueryKey(slug, conversationId);

  return useMutation<Message, Error, ReplyVariables, ReplyContext>({
    mutationFn: async ({ body }) => {
      // Offline path — queue the send and return a sentinel "queued"
      // message so the optimistic bubble stays in the thread. The reply
      // queue's online listener will replay this when connectivity comes
      // back.
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        const optimisticId = `optimistic_${Date.now()}`;
        enqueueReply({
          optimisticId,
          slug,
          conversationId,
          body,
          queuedAtIso: new Date().toISOString(),
        });
        // Return a sentinel that the success handler will recognize and
        // leave in place (instead of swapping for a real server message).
        // The presence of __queued tells downstream code to render with
        // a "Sending when online" affordance.
        const sentinel = makeQueuedMessage(optimisticId);
        sentinel.body = body;
        return sentinel;
      }
      return postReply(conversationId, slug, body);
    },

    onMutate: async ({ body }) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<ConversationDetail>(key);
      const optimisticId = `optimistic_${Date.now()}`;

      if (previous) {
        const optimisticMessage: Message = {
          id: optimisticId,
          body,
          direction: 'out',
          source: 'sms',
          timestamp_iso: new Date().toISOString(),
          sender_name: 'You',
        };
        qc.setQueryData<ConversationDetail>(key, {
          ...previous,
          messages: [...previous.messages, optimisticMessage],
        });
      }

      return { optimisticId, previous };
    },

    onError: (_err, _vars, context) => {
      if (context?.previous) {
        qc.setQueryData(key, context.previous);
      }
    },

    onSuccess: (serverMessage, _vars, context) => {
      const optimisticId = context?.optimisticId;
      qc.setQueryData<ConversationDetail>(key, (curr) => {
        if (!curr) return curr;
        return {
          ...curr,
          messages: curr.messages.map((m) =>
            optimisticId && m.id === optimisticId ? serverMessage : m,
          ),
        };
      });
      // 2026-06-01 — invalidate the conversations list query so the Drip
      // pill on the row clears immediately. handlePwaReply in the Worker
      // does an implicit takeover (adds quote_followup_stop, removes drip
      // trigger tags) on every reply, but the list query has its own React
      // Query cache and won't refetch without this invalidation. Partial
      // key matches all list variants. Doesn't touch detail data — the
      // setQueryData above keeps the thread cache exact.
      void qc.invalidateQueries({ queryKey: ['conversations', slug] });
    },

    // Don't auto-invalidate the detail — the optimistic swap above keeps
    // it exact. A separate refetch on focus (in useConversation) handles
    // drift.
  });
}
